import React, { useMemo, useRef } from "react";
import { useGame } from "../../../src/stores/useGame";
import { useLevelProgress } from "../../stores/useLevelProgress";
import { useUI } from "../../stores/useUI";

const C = {
  cream: '#FBF2D3',
  dark: '#521616',
  dot: '#573030',
  mono: '"IBM Plex Mono", monospace',
};

// Scale factor: how many pixels on minimap correspond to 1 world unit
// Adjustable if needed
const MINIMAP_SCALE = 2;
const MINIMAP_SIZE = 174; // Diameter in pixels
const MINIMAP_RADIUS = MINIMAP_SIZE / 2;

const Minimap = () => {
  const currentMap = useUI((state) => state.currentMap);
  const playerPosition = useGame((state) => state.playerPosition);
  const playerRotation = useGame((state) => state.playerRotation);
  const cameraRotation = useGame((state) => state.cameraRotation);
  const npcPositions = useGame((state) => state.npcPositions);
  
  const { getMapData, isNPCCompleted } = useLevelProgress();
  
  const mapData = useMemo(() => {
    const allData = getMapData();
    return currentMap ? allData[currentMap] : null;
  }, [currentMap, getMapData]);

  // Handle camera rotation continuity to prevent spin on wrap-around
  const lastCameraRotation = useRef(cameraRotation);
  const cumulativeCameraRotation = useRef(cameraRotation);

  // Calculate the shortest path diff
  let diff = cameraRotation - lastCameraRotation.current;
  if (diff > Math.PI) diff -= 2 * Math.PI;
  else if (diff < -Math.PI) diff += 2 * Math.PI;
  
  cumulativeCameraRotation.current += diff;
  lastCameraRotation.current = cameraRotation;

  // If no map or player position, don't render
  if (!currentMap || !mapData || !playerPosition) {
    return null;
  }

  // Render NPCs relative to player
  // World coordinates: X (East/West), Z (North/South)
  // Screen coordinates: X (Right/Left), Y (Down/Up) -> +Z is Down, -Z is Up (North)
  const renderNPCs = () => {
    return mapData.npcs.map((npc) => {
      const isCompleted = isNPCCompleted(currentMap, npc.id);
      
      // Get dynamic position if available, otherwise use static initial position
      const currentPos = npcPositions[npc.id] || { 
        x: npc.position[0], 
        y: npc.position[1], 
        z: npc.position[2] 
      };

      // Calculate relative position
      const relX = currentPos.x - playerPosition.x;
      const relZ = currentPos.z - playerPosition.z;
      
      // Scale to minimap pixels
      // Invert X and Z because:
      // 1. Ecctrl uses +Z as Forward, but we want Forward to be Up (-Y) on map (so mapY = -relZ)
      // 2. Ecctrl Right is -X, but we want Right to be Right (+X) on map (so mapX = -relX)
      let mapX = -relX * MINIMAP_SCALE;
      let mapY = -relZ * MINIMAP_SCALE;
      
      // Clamp to circle edge (keep visible)
      const dist = Math.sqrt(mapX * mapX + mapY * mapY);
      const maxDist = MINIMAP_RADIUS - 6; // Radius - dot size/padding

      if (dist > maxDist) {
        const scale = maxDist / dist;
        mapX *= scale;
        mapY *= scale;
      }
      
      return (
        <div key={npc.id} style={{ position: "absolute", left: `${MINIMAP_RADIUS + mapX}px`, top: `${MINIMAP_RADIUS + mapY}px`, transform: "translate(-50%, -50%)", zIndex: 2 }}>
          {/* Outer ring */}
          <div style={{
            position: "absolute",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            border: `1px solid ${C.dot}`,
            transform: "translate(-50%, -50%)",
            opacity: isCompleted ? 1 : 0.4,
          }} />
          {/* Inner dot */}
          <div style={{
            position: "absolute",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: C.dot,
            border: `1px solid ${C.dot}`,
            transform: "translate(-50%, -50%)",
            opacity: isCompleted ? 1 : 0.4,
          }} />
        </div>
      );
    });
  };

  // Calculate rotation in degrees for CSS using cumulative value
  const rotationDeg = (cumulativeCameraRotation.current * 180) / Math.PI;

  // Determine player indicator rotation relative to map
  // We normalize the relative rotation to -180...180 for shortest rotation path if needed
  // (Though player indicator doesn't have transition, so it snaps correctly regardless)
  // Note: CSS rotation is CW, Three.js rotation is CCW.
  // If player rotates +90 (CCW, facing Left on map), we want indicator to rotate -90 (CCW/Left).
  // So we need to negate the relative rotation.
  let playerRelativeRotationDeg = -((playerRotation - cameraRotation) * 180) / Math.PI;
  
  // Normalize to -180..180 just for cleanliness
  while (playerRelativeRotationDeg > 180) playerRelativeRotationDeg -= 360;
  while (playerRelativeRotationDeg < -180) playerRelativeRotationDeg += 360;

  return (
    <div style={styles.container}>
      <div style={styles.mask}>
        <div 
            style={{
                ...styles.rotatingLayer,
                transform: `rotate(${rotationDeg}deg)`
            }}
        >
            {/* Grid/Background lines for effect */}
            {/* <div style={styles.grid} /> */}
            
            {/* North Indicator (Fixed to world North) */}
            <div style={styles.northIndicator}>N</div>
            
            {/* NPCs */}
            {renderNPCs()}
        </div>
        
        {/* Player Indicator (Triangle) */}
        <div style={{
          ...styles.playerIndicator,
          transform: `translate(-50%, -50%) rotate(${playerRelativeRotationDeg}deg)`,
        }} />
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: "absolute" as const,
    bottom: "20px",
    right: "20px",
    width: `${MINIMAP_SIZE}px`,
    height: `${MINIMAP_SIZE}px`,
    zIndex: 100,
    pointerEvents: "none" as const,
  },
  mask: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundColor: C.cream,
    border: `1px solid ${C.dark}`,
    boxShadow: "inset -3px -3px 0px rgba(212, 28, 28, 0.25)",
    position: "relative" as const,
  },
  rotatingLayer: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    transformOrigin: "center center",
    transition: "transform 0.1s linear",
  },
  grid: {
    position: "absolute" as const,
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    backgroundColor: "transparent",
    backgroundImage: `linear-gradient(to right, rgba(82, 22, 22, 0.08) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(82, 22, 22, 0.08) 1px, transparent 1px)`,
    backgroundSize: "20px 20px",
  },
  playerIndicator: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    width: "0",
    height: "0",
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderBottom: `14px solid ${C.dot}`,
    zIndex: 10,
  },
  northIndicator: {
    position: "absolute" as const,
    top: "-20px",
    left: "50%",
    transform: "translateX(-50%)",
    color: C.dark,
    fontFamily: C.mono,
    fontWeight: "bold",
    fontSize: "12px",
    zIndex: 5,
  },
};

export default Minimap;
